import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { fmtDate, formatApiError } from "@/lib/apiClient";
import EmployeeLayout from "@/layouts/EmployeeLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Paperclip, FileText, Trash2, Plus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { logoSrc } from "@/components/BrandLogo";

const DOC_TYPES = ["Employment contract", "Safe Pass", "Manual handling cert", "Electrical qualification", "Driving licence", "Training certificate", "Medical certificate", "Other"];

function Row({ label, value }) {
  return <div className="flex justify-between py-2 border-b border-zinc-100 text-sm"><span className="text-zinc-500">{label}</span><span className="font-medium text-right">{value || "—"}</span></div>;
}

function docStatus(expiry) {
  if (!expiry) return { label: "No expiry", cls: "bg-zinc-100 text-zinc-500" };
  const days = Math.ceil((new Date(expiry) - new Date()) / 86400000);
  if (days < 0) return { label: "Expired", cls: "bg-rose-100 text-rose-700" };
  if (days <= 60) return { label: `Expires in ${days}d`, cls: "bg-amber-100 text-amber-800" };
  return { label: "Valid", cls: "bg-emerald-100 text-emerald-700" };
}

export default function EmployeeProfile() {
  const { user, employee, refresh } = useAuth();
  const [docs, setDocs] = useState([]);
  const [comp, setComp] = useState(null);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [contact, setContact] = useState({ mobile: "", emergency_contact: "" });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ doc_type: "Safe Pass", expiry: "", file_url: null, name: "" });
  const e = employee || {};

  const load = useCallback(() => { if (e.id) { api.get(`/employees/${e.id}/documents`).then((r) => setDocs(r.data)).catch(() => {}); api.get(`/employees/${e.id}/compliance`).then((r) => setComp(r.data)).catch(() => {}); } }, [e.id]);
  useEffect(() => { load(); }, [load]);

  const openEdit = () => { setContact({ mobile: e.mobile || "", emergency_contact: e.emergency_contact || "" }); setEditOpen(true); };
  const saveContact = async () => {
    setSaving(true);
    try { await api.put("/employees/me/contact", contact); await refresh(); toast.success("Details updated"); setEditOpen(false); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onFile = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("File must be under 10MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const res = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, file_url: res.data.file_url, name: f.name }));
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const upload = async () => {
    if (!form.file_url) { toast.error("Please choose a file"); return; }
    try { await api.post(`/employees/${e.id}/documents`, form); toast.success("Document uploaded"); setOpen(false); setForm({ doc_type: "Safe Pass", expiry: "", file_url: null, name: "" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/employees/${e.id}/documents/${id}`); load(); };
  const view = (d) => { const src = logoSrc(d.file_url || d.file); if (src) window.open(src, "_blank"); };

  return (
    <EmployeeLayout title="Profile">
      <div className="bg-white border border-zinc-200 rounded-md p-4 mb-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-14 w-14 rounded-full bg-slate-900 text-amber-500 flex items-center justify-center font-heading font-black text-xl">
            {(e.first_name?.[0] || "") + (e.surname?.[0] || "")}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg">{e.first_name} {e.surname}</div>
            <div className="text-sm text-zinc-500">{e.grade || e.job_title}</div>
          </div>
          <button data-testid="edit-contact-btn" onClick={openEdit} className="inline-flex items-center gap-1 text-sm text-amber-600 font-semibold shrink-0"><Pencil className="h-4 w-4" /> Edit</button>
        </div>
        <Row label="Employee no." value={e.employee_number} />
        <Row label="Email" value={user?.email} />
        <Row label="Mobile" value={e.mobile} />
        <Row label="Emergency contact" value={e.emergency_contact} />
        <Row label="Manager" value={e.manager} />
        <Row label="Employment type" value={e.employment_type} />
        <Row label="Start date" value={fmtDate(e.employment_start)} />
        <Row label="PRSI class" value={e.prsi_class} />
      </div>

      {comp && (
        <div className="bg-white border border-zinc-200 rounded-md p-4 mb-4" data-testid="my-compliance">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-500">Profile compliance</span>
            <span className={`text-lg font-black ${comp.compliant ? "text-emerald-600" : comp.safe_pass_ok ? "text-amber-600" : "text-rose-600"}`}>{comp.pct}%</span>
          </div>
          <div className="h-2 rounded-full bg-zinc-100 overflow-hidden"><div className={`h-full ${comp.compliant ? "bg-emerald-500" : comp.safe_pass_ok ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${comp.pct}%` }} /></div>
          <div className="text-xs mt-2">Safe Pass: <span className={comp.safe_pass_ok ? "text-emerald-600 font-semibold" : "text-rose-600 font-semibold"}>{comp.safe_pass_ok ? "Valid" : (comp.safe_pass_status === "expired" ? "Expired — renew now" : "Not uploaded")}</span></div>
          {!comp.compliant && <div className="text-xs text-zinc-500 mt-1">To reach 100%: {comp.missing.join(", ")}</div>}
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xs font-bold uppercase tracking-wider text-zinc-500">My documents</h2>
        <button data-testid="upload-doc-btn" onClick={() => setOpen(true)} className="inline-flex items-center gap-1 text-sm text-amber-600 font-semibold"><Plus className="h-4 w-4" /> Upload</button>
      </div>
      <div className="space-y-2">
        {docs.length === 0 && <div className="text-sm text-zinc-400">No documents on file.</div>}
        {docs.map((d) => {
          const st = docStatus(d.expiry);
          return (
            <div key={d.id} data-testid={`doc-${d.id}`} className="bg-white border border-zinc-200 rounded-md p-3 flex items-center justify-between">
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{d.doc_type || d.name}</div>
                <div className="text-xs text-zinc-500">{d.expiry ? `Expires ${fmtDate(d.expiry)}` : "No expiry date"}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
                {d.file_url && <button data-testid={`doc-view-${d.id}`} onClick={() => view(d)} className="text-amber-600"><FileText className="h-4 w-4" /></button>}
                <button data-testid={`doc-del-${d.id}`} onClick={() => del(d.id)} className="text-rose-400"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit contact details</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Mobile number</Label><Input data-testid="edit-mobile" value={contact.mobile} onChange={(ev) => setContact({ ...contact, mobile: ev.target.value })} placeholder="+353 ..." className="h-12 mt-1" /></div>
            <div><Label>Emergency contact</Label><Input data-testid="edit-emergency" value={contact.emergency_contact} onChange={(ev) => setContact({ ...contact, emergency_contact: ev.target.value })} placeholder="Name (phone)" className="h-12 mt-1" /></div>
            <Button data-testid="save-contact" onClick={saveContact} disabled={saving} className="w-full h-12 bg-slate-900 text-white">{saving ? "Saving…" : "Save changes"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Upload document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Document type</Label>
              <Select value={form.doc_type} onValueChange={(v) => setForm({ ...form, doc_type: v })}>
                <SelectTrigger data-testid="doc-type" className="h-12 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Expiry date (optional)</Label><Input data-testid="doc-expiry" type="date" value={form.expiry} onChange={(e2) => setForm({ ...form, expiry: e2.target.value })} className="h-12 mt-1" /></div>
            <div>
              <Label>File (photo or PDF)</Label>
              <label className="mt-1 flex items-center gap-2 h-12 px-3 border border-dashed border-zinc-300 rounded-md cursor-pointer text-sm text-zinc-500 hover:border-amber-400">
                <Paperclip className="h-4 w-4" /><span className="truncate">{uploading ? "Uploading…" : (form.name || "Choose file")}</span>
                <input data-testid="doc-file" type="file" accept="image/*,application/pdf" onChange={onFile} className="hidden" />
              </label>
            </div>
            <Button data-testid="doc-submit" onClick={upload} className="w-full h-12 bg-slate-900 text-white">Upload</Button>
          </div>
        </DialogContent>
      </Dialog>
    </EmployeeLayout>
  );
}
